import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, ArrowCounterClockwise } from "@phosphor-icons/react";

const BRL = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;

export default function PaymentsPage() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "teacher";
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", amount: 350, due_date: "", month_ref: "", status: "pending" });

  async function load() {
    const r = await api.get("/payments"); setPayments(r.data);
    if (user?.role !== "student") {
      const s = await api.get("/students"); setStudents(s.data);
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    try {
      if (!form.student_id || !form.due_date || !form.month_ref) { toast.error("Preencha todos os campos"); return; }
      await api.post("/payments", form);
      setOpen(false); setForm({ student_id: "", amount: 350, due_date: "", month_ref: "", status: "pending" });
      toast.success("Pagamento criado"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }
  async function markPaid(p) {
    try { await api.patch(`/payments/${p.id}`, { status: "paid" }); toast.success("Marcado como pago"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }
  async function markPending(p) {
    try { await api.patch(`/payments/${p.id}`, { status: "pending" }); toast.success("Marcado como pendente"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }
  async function remove(p) {
    if (!confirm(`Excluir esta cobrança (${p.month_ref} • ${BRL(p.amount)})?`)) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Cobrança excluída"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  }

  const studentName = (sid) => students.find(s => s.id === sid)?.name || "—";

  return (
    <div className="p-4 sm:p-8 lg:p-10 max-w-6xl mx-auto space-y-6">
      <header className="flex items-end justify-between fade-up flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1E5FFF]">Pagamentos</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mt-2">Mensalidades</h1>
          {user?.role === "teacher" && (
            <p className="text-zinc-500 text-sm mt-1">Você controla apenas as mensalidades dos seus alunos.</p>
          )}
        </div>
        {isManager && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="pay-new" className="bg-[#002FA7] brutal-shadow"><Plus size={16} className="mr-1"/>Novo pagamento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Registrar pagamento</DialogTitle>
                <DialogDescription>Crie a cobrança do aluno. Vencidos bloqueiam acesso automaticamente.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Aluno</Label>
                  <Select value={form.student_id} onValueChange={v => setForm({...form, student_id: v})}>
                    <SelectTrigger data-testid="pay-student"><SelectValue placeholder="Selecione"/></SelectTrigger>
                    <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Valor</Label><Input data-testid="pay-amount" type="number" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})}/></div>
                <div><Label>Vencimento</Label><Input data-testid="pay-due" type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}/></div>
                <div><Label>Mês ref (ex 2026-02)</Label><Input data-testid="pay-month" value={form.month_ref} onChange={e => setForm({...form, month_ref: e.target.value})}/></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="paid">Pago</SelectItem>
                      <SelectItem value="overdue">Vencido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button data-testid="pay-submit" onClick={create} className="w-full bg-[#002FA7]">Salvar</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </header>

      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden fade-up">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            {isManager && <th className="py-3 px-6">Aluno</th>}
            <th className={isManager ? "" : "py-3 px-6"}>Mês</th>
            <th>Vencimento</th>
            <th>Valor</th>
            <th>Status</th>
            {isManager && <th className="pr-6 text-right">Ações</th>}
          </tr></thead>
          <tbody>
            {payments.length === 0 && <tr><td colSpan={isManager ? 6 : 4} className="text-center py-16 text-zinc-400">Nenhum pagamento registrado.</td></tr>}
            {payments.map(p => (
              <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                {isManager && <td className="py-3 px-6 font-medium">{studentName(p.student_id)}</td>}
                <td className={`font-medium ${isManager ? "" : "py-3 px-6"}`}>{p.month_ref}</td>
                <td className="font-mono-num text-zinc-500">{p.due_date}</td>
                <td className="font-mono-num font-bold">{BRL(p.amount)}</td>
                <td>
                  <span className={`inline-block px-2 py-1 text-xs font-bold rounded ${
                    p.status === "paid" ? "bg-emerald-100 text-[#057A55]" :
                    p.status === "overdue" ? "bg-red-100 text-[#E02424]" :
                    "bg-yellow-100 text-yellow-800"
                  }`}>{p.status.toUpperCase()}</span>
                </td>
                {isManager && (
                  <td className="pr-6 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.status !== "paid" ? (
                        <Button data-testid={`mark-paid-${p.id}`} size="sm" variant="ghost" onClick={() => markPaid(p)}
                                title="Marcar como pago" className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#12B76A] hover:bg-[#EAF7EF]">
                          <CheckCircle size={16} weight="fill" />
                        </Button>
                      ) : (
                        <Button data-testid={`mark-pending-${p.id}`} size="sm" variant="ghost" onClick={() => markPending(p)}
                                title="Marcar como pendente" className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#F79009] hover:bg-[#FFF6EA]">
                          <ArrowCounterClockwise size={14} weight="bold" />
                        </Button>
                      )}
                      <Button data-testid={`del-payment-${p.id}`} size="sm" variant="ghost" onClick={() => remove(p)}
                              title="Excluir cobrança" className="rounded-full h-9 px-2 text-zinc-400 hover:text-[#FF3D71] hover:bg-[#FFE9EE]">
                        <Trash size={14} weight="bold" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
